import { writable, type Writable } from 'svelte/store';
import { browser } from '$app/environment';
import linkifyHtml from 'linkify-html';
import { localStorageExpiry } from './constants';
import { onDestroy } from 'svelte';

export const copyToClipBoard = (text: string | undefined) => {
  if (!text) return;
  navigator?.clipboard?.writeText(text);
};

export const clickOutside = (node: HTMLElement, callback: () => void) => {
  const handleClick = (event: MouseEvent) => {
    if (!node.contains(event.target as Node)) {
      // node.dispatchEvent(new CustomEvent('outclick'));
      callback();
    }
  };

  document.addEventListener('click', handleClick, true);

  return {
    destroy() {
      document.removeEventListener('click', handleClick, true);
    },
  };
};

export type CountdownTimer = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export function countdownTimer(endDate: Date): Writable<CountdownTimer> {
  if (!(endDate instanceof Date)) {
    throw new Error('endDate must be a Date object');
  }

  const countdown = writable({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  function updateCountdown() {
    const now = new Date().getTime();
    const distance = endDate.getTime() - now;

    if (distance < 0) {
      countdown.set({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    } else {
      countdown.set({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        ),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
    }
  }

  // Initial update
  updateCountdown();

  const timer = setInterval(updateCountdown, 1000);

  onDestroy(() => clearInterval(timer));

  return countdown;
}

export const persistLocalStorageForm = <T>(name: string, data: T) => {
  if (!browser) return;
  localStorage.setItem(
    name,
    JSON.stringify({ data, expiry: Date.now() + localStorageExpiry }),
  );
};
export const retrieveLocalStorageForm = <T>(name: string): T => {
  if (!browser) return null as T;
  if (localStorage.getItem(name)) {
    const { data } = JSON.parse(localStorage.getItem(name) as string);
    return data as T;
  }
  return null as T;
};
export const clearLocalStorageForm = (name: string) => {
  if (!browser) return;
  localStorage.removeItem(name);
};

export const clearExpiredProjectsLocalStorage = () => {
  if (!browser) return;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.includes('project-submission')) {
      try {
        const expiry =
          JSON.parse(localStorage.getItem(key as string) as string).expiry ?? 0;
        if (Date.now() > expiry) {
          localStorage.removeItem(key as string);
        }
      } catch (e) {
        console.log('Error parsing project submission local storage item', e);
      }
    }
  }
};

export const isScrolled = (threshold: number) => {
  // biome-ignore lint/style/noParameterAssign: TODO: Don't reassign parameter
  threshold = threshold ?? 500;
  if (browser) {
    window.onscroll = () => {
      if (
        document.body.scrollTop > threshold ||
        document.documentElement.scrollTop > threshold
      ) {
        return true;
      }
      return false;
    };
  }
};

export const timeSince = (date: Date) => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

  let interval = seconds / 31536000;

  let floor: number;
  if (interval > 1) {
    floor = Math.floor(interval);
    return floor + (floor === 1 ? ' year' : ' years');
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    floor = Math.floor(interval);
    return floor + (floor === 1 ? ' month' : ' months');
  }
  interval = seconds / 86400;
  if (interval > 1) {
    floor = Math.floor(interval);
    return floor + (floor === 1 ? ' day' : ' days');
  }
  interval = seconds / 3600;
  if (interval > 1) {
    floor = Math.floor(interval);
    return floor + (floor === 1 ? ' hour' : ' hours');
  }
  interval = seconds / 60;
  if (interval > 1) {
    floor = Math.floor(interval);
    return floor + (floor === 1 ? ' minute' : ' minutes');
  }
  floor = Math.floor(seconds);
  return floor + (floor === 1 ? ' second' : ' seconds');
};

// biome-ignore lint/suspicious/noExplicitAny: TODO: fix this missing type
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  return (...args: Parameters<T>): void => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

export const isPreviousDay = (date1: Date, date2: Date): boolean =>
  new Date(date1.getFullYear(), date1.getMonth(), date1.getDate()).getTime() ===
  new Date(date2.getFullYear(), date2.getMonth(), date2.getDate()).getTime() -
    24 * 60 * 60 * 1000;

export const isMobileDevice = (): boolean =>
  browser && window.innerWidth <= 640;

export const scrollToTop = () => {
  browser &&
    setTimeout(() => {
      window?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }, 100);
};

export const linkify = (text: string): string => {
  return linkifyHtml(text, {
    defaultProtocol: 'https',
    target: '_blank',
    rel: 'noopener noreferrer nofollow',
    className: 'relative z-10 text-mint-dark-9',
  });
};

export function base64UrlEncode(str: string): string {
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlDecode(str: string): string {
  // biome-ignore lint/style/noParameterAssign: TODO: Don't reassign parameter
  str = str.replace(/-/g, '+').replace(/_/g, '/');
  while (str.length % 4) {
    // biome-ignore lint/style/noParameterAssign: TODO: Don't reassign parameter
    str += '=';
  }
  return atob(str);
}
